import { TAB_ORGANIZATION_STORAGE_KEY } from '@/constants/storage-keys';

let currentOrganizationId: number | null = null;

export const getCurrentOrganizationId = (): number | null =>
  currentOrganizationId;

export const setCurrentOrganizationId = (
  organizationId: number | null,
): void => {
  currentOrganizationId = organizationId;
};

export const ORGANIZATION_HEADER = 'X-Coretas-Organization';

// Where a header cannot go: the API reads it on streams and downloads, the app on a page link.
export const ORGANIZATION_QUERY_PARAM = 'org';

// Blocked storage keeps the pin for this page only; staff are refused on any request that names none.
let pageTabOrganizationId: number | null = null;

// The organization this tab names on every request; sessionStorage, so other tabs keep their own.
export const getTabOrganizationId = (): number | null => {
  try {
    const raw = window.sessionStorage.getItem(TAB_ORGANIZATION_STORAGE_KEY);
    return raw !== null && /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
  } catch {
    return pageTabOrganizationId;
  }
};

// The organization a request from this tab acts in: the one it names, else the user's own.
export const getActingOrganizationId = (): number | null =>
  getTabOrganizationId() ?? getCurrentOrganizationId();

export const setTabOrganizationId = (organizationId: number): void => {
  pageTabOrganizationId = organizationId;
  try {
    window.sessionStorage.setItem(
      TAB_ORGANIZATION_STORAGE_KEY,
      String(organizationId),
    );
  } catch {
    /* private mode — the pin lasts until this page goes, then /auth/me pins it again */
  }
};

let pendingLeave: (() => void) | null = null;

const dropPendingLeave = (): void => {
  if (pendingLeave === null) return;
  window.removeEventListener('pagehide', pendingLeave);
  pendingLeave = null;
};

// A "Leave site?" prompt can cancel the load, so the page keeps naming its organization until it goes.
export const whenThisPageLeaves = (change: () => void): void => {
  dropPendingLeave();
  pendingLeave = change;
  window.addEventListener('pagehide', change, { once: true });
};

// Also drops a pending change, so it never outlives the identity that armed it.
export const clearTabOrganizationId = (): void => {
  pageTabOrganizationId = null;
  try {
    dropPendingLeave();
    window.sessionStorage.removeItem(TAB_ORGANIZATION_STORAGE_KEY);
  } catch {
    /* nothing stored to clear */
  }
};

export const tabOrganizationHeaders = (): Record<string, string> => {
  const organizationId = getTabOrganizationId();
  return organizationId === null
    ? {}
    : { [ORGANIZATION_HEADER]: String(organizationId) };
};

// For a URL no header reaches: EventSource, a download, a new-tab or copied app link all read `?org=`.
export const withTabOrganization = (url: string): string => {
  const organizationId = getTabOrganizationId();
  if (organizationId === null) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${ORGANIZATION_QUERY_PARAM}=${organizationId}`;
};
